import { Outlet, Link, NavLink } from "react-router-dom";
import { Sparkles, Heart, BookOpenText, Home as HomeIcon, Users, Trophy, BookOpen } from "lucide-react";
import SubscribeForm from "@/components/SubscribeForm";

const navItems = [
    { to: "/", label: "Home", icon: HomeIcon, testid: "nav-home" },
    { to: "/lessons", label: "Lessons", icon: BookOpenText, testid: "nav-lessons" },
    { to: "/bible", label: "Bible", icon: BookOpen, testid: "nav-bible" },
    { to: "/rewards", label: "Rewards", icon: Trophy, testid: "nav-rewards" },
    { to: "/community", label: "Community", icon: Users, testid: "nav-community" },
    { to: "/about", label: "About", icon: Heart, testid: "nav-about" },
];

export default function Layout() {
    return (
        <div className="min-h-screen flex flex-col bg-cream">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-cream border-b-2 border-ink" data-testid="site-header">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-6">
                    <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                        <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-sun border-2 border-ink shadow-sticker-sm group-hover:rotate-[-6deg] transition-transform">
                            <Sparkles className="w-6 h-6 text-ink" strokeWidth={2.5} />
                        </span>
                        <span className="font-heading font-bold text-2xl tracking-tight">
                            2nd Mile Kids
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map(({ to, label, icon: Icon, testid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/"}
                                data-testid={testid}
                                className={({ isActive }) =>
                                    `font-heading text-sm font-semibold px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2 ${
                                        isActive
                                            ? "bg-ink text-cream border-ink"
                                            : "border-transparent hover:border-ink hover:bg-sun"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" strokeWidth={2.5} />
                                {label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/lessons"
                        data-testid="header-cta"
                        className="btn-sticker bg-coral text-white text-sm hidden sm:inline-flex"
                    >
                        Today's Lesson
                    </Link>
                </div>

                {/* Mobile nav row */}
                <nav className="md:hidden border-t-2 border-ink/10 bg-white">
                    <div className="max-w-7xl mx-auto px-3 py-2 flex items-center gap-1 overflow-x-auto">
                        {navItems.map(({ to, label, icon: Icon, testid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/"}
                                data-testid={`${testid}-mobile`}
                                className={({ isActive }) =>
                                    `whitespace-nowrap text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full border-2 flex items-center gap-1.5 ${
                                        isActive ? "bg-ink text-cream border-ink" : "border-ink/20"
                                    }`
                                }
                            >
                                <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                                {label}
                            </NavLink>
                        ))}
                    </div>
                </nav>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <footer className="border-t-2 border-ink bg-white mt-20" data-testid="site-footer">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-mint border-2 border-ink">
                                <Sparkles className="w-5 h-5 text-ink" strokeWidth={2.5} />
                            </span>
                            <span className="font-heading font-bold text-xl">2nd Mile Kids</span>
                        </div>
                        <p className="text-sm text-ink/70 max-w-xs">
                            Daily, kid-friendly Bible lessons (ESV) and Christ-centered ideas to love your neighbor and go the second mile (Matthew 5:41).
                        </p>
                    </div>
                    <div>
                        <p className="font-heading font-bold mb-3">Explore</p>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/lessons" className="hover:underline">All Lessons</Link></li>
                            <li><Link to="/bible" className="hover:underline">Bible Navigator</Link></li>
                            <li><Link to="/rewards" className="hover:underline">Rewards</Link></li>
                            <li><Link to="/community" className="hover:underline">Community Hub</Link></li>
                            <li><Link to="/family-pack" className="hover:underline">Family Pack</Link></li>
                            <li><Link to="/faith" className="hover:underline" data-testid="footer-faith-link">Statement of Faith</Link></li>
                            <li><Link to="/about" className="hover:underline">About</Link></li>
                        </ul>
                    </div>
                    <div>
                        <SubscribeForm variant="footer" />
                    </div>
                </div>
                <div className="border-t-2 border-ink/10 py-4 text-center text-xs text-ink/60 font-accent text-lg">
                    Go the second mile ✝️ © {new Date().getFullYear()} 2nd Mile Kids
                </div>
            </footer>
        </div>
    );
}
